(() => {
  const SECTION_SELECTOR = '[data-tabs-section]';
  const TAB_SELECTOR = '.section-tabs__tab';
  const PANEL_SELECTOR = '.section-tabs__panel';
  const MOBILE_SELECT_SELECTOR = '.section-tabs__mobile-select';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setIndicatorPosition = (section) => {
    const indicator = section.querySelector('.section-tabs__indicator');
    const activeTab = section.querySelector(`${TAB_SELECTOR}.is-active`);

    if (!indicator || !activeTab) return;

    indicator.style.setProperty('--section-tabs-indicator-x', `${activeTab.offsetLeft}px`);
    indicator.style.setProperty('--section-tabs-indicator-width', `${activeTab.offsetWidth}px`);
    indicator.style.opacity = '1';
  };

  const scrollTabIntoView = (tab) => {
    if (!tab) return;

    tab.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const activateTab = (section, nextTab, options = {}) => {
    if (!section || !nextTab) return;

    const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
    const panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
    const targetPanelId = nextTab.getAttribute('aria-controls');

    tabs.forEach((tab) => {
      const isActive = tab === nextTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.id === targetPanelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    const mobileSelect = section.querySelector(MOBILE_SELECT_SELECTOR);
    if (mobileSelect && mobileSelect.value !== targetPanelId) {
      mobileSelect.value = targetPanelId;
    }

    window.requestAnimationFrame(() => {
      setIndicatorPosition(section);

      if (options.scroll !== false) {
        scrollTabIntoView(nextTab);
      }
    });

    if (options.focus) {
      nextTab.focus();
    }
  };

  const handleKeydown = (event, section, currentTab) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
    const currentIndex = tabs.indexOf(currentTab);
    if (currentIndex === -1) return;

    event.preventDefault();

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }

    activateTab(section, tabs[nextIndex], { focus: true });
  };

  const initTabsSection = (section) => {
    if (!(section instanceof Element) || section.dataset.tabsInitialized === 'true') {
      return;
    }

    const tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
    if (!tabs.length) return;

    section.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest(TAB_SELECTOR) : null;
      if (!tab || !section.contains(tab)) return;

      activateTab(section, tab);
    });

    section.addEventListener('keydown', (event) => {
      const tab = event.target instanceof Element ? event.target.closest(TAB_SELECTOR) : null;
      if (!tab || !section.contains(tab)) return;

      handleKeydown(event, section, tab);
    });

    section.addEventListener('change', (event) => {
      const mobileSelect = event.target instanceof Element ? event.target.closest(MOBILE_SELECT_SELECTOR) : null;
      if (!mobileSelect || !section.contains(mobileSelect)) return;

      const currentTabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
      const nextTab = currentTabs.find((tab) => tab.getAttribute('aria-controls') === mobileSelect.value);
      if (!nextTab) return;

      activateTab(section, nextTab, { scroll: false });
    });

    const initialTab =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true' || tab.classList.contains('is-active')) ||
      tabs[0];

    activateTab(section, initialTab, { scroll: false });
    section.dataset.tabsInitialized = 'true';
  };

  const initAllTabsSections = (root = document) => {
    if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
      initTabsSection(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(SECTION_SELECTOR).forEach((section) => initTabsSection(section));
    }
  };

  const updateAllIndicators = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => setIndicatorPosition(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllTabsSections());
  } else {
    initAllTabsSections();
  }

  window.addEventListener('resize', () => window.requestAnimationFrame(updateAllIndicators));

  document.addEventListener('shopify:section:load', (event) => {
    initAllTabsSections(event.target instanceof Element ? event.target : document);
    window.requestAnimationFrame(updateAllIndicators);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target instanceof Element ? event.target.closest(PANEL_SELECTOR) : null;
    if (!panel) return;

    const section = panel.closest(SECTION_SELECTOR);
    const nextTab = section?.querySelector(`${TAB_SELECTOR}[aria-controls="${panel.id}"]`);
    if (!section || !nextTab) return;

    activateTab(section, nextTab, { scroll: false });
  });
})();
